import { AlertTriangle } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { CancellationPolicy } from "@/hooks/useCancellationPolicy";
import { CREDIT_VALUE_IN_POUNDS } from "@/configs";
import { capture as posthogCapture } from "@/services/posthog";
import { userTicketQueryKey, userTicketsBookedQueryKey } from "@/lib/ticketQueryKeys";

interface TicketCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  eventId: string;
  paymentMethod?: string | null;
  creditsSpent?: number | null;
  totalPrice?: number | null;
  policy: CancellationPolicy;
}

export function TicketCancelDialog({
  open,
  onOpenChange,
  ticketId,
  eventId,
  paymentMethod,
  creditsSpent,
  totalPrice,
  policy,
}: TicketCancelDialogProps) {
  const queryClient = useQueryClient();

  const estimatedCredits =
    (creditsSpent ?? 0) > 0
      ? (creditsSpent as number)
      : Math.max(0, Math.floor((totalPrice ?? 0) / CREDIT_VALUE_IN_POUNDS));

  const stripePaid = paymentMethod === "stripe" && (creditsSpent ?? 0) === 0;

  // No credits spent and no cash price — e.g. the free Nimi Friends Dinner Club
  // seat (100% off). There is genuinely nothing to refund, so the dialog says so
  // instead of showing "0 credits", which reads like a broken number.
  const isFreeBooking = (creditsSpent ?? 0) === 0 && (totalPrice ?? 0) === 0;

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("cancel_ticket_by_user", {
        _ticket_id: ticketId,
      });
      if (error) throw error;
      return data as {
        refunded: boolean;
        credits_refunded: number;
      };
    },
    onSuccess: (result) => {
      posthogCapture("booking_cancelled", {
        event_id: eventId,
        ticket_id: ticketId,
        refunded: result.refunded,
        refund_credits: result.credits_refunded,
        branch: policy.refundBranch,
      });
      toast({
        title: result.refunded ? "Booking cancelled" : "Booking released",
        description:
          result.credits_refunded > 0
            ? `${result.credits_refunded} credits have been added back to your account.`
            : result.refunded
              ? "This booking was free, so there's nothing to refund. Your seat has been released."
              : "Your seat has been released. No refund was issued for this late cancellation.",
      });
      queryClient.invalidateQueries({ queryKey: userTicketQueryKey(eventId) });
      queryClient.invalidateQueries({ queryKey: userTicketsBookedQueryKey(eventId) });
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["user-membership"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["my-tickets"] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't cancel booking",
        description: error?.message || "Please try again or contact support.",
        variant: "destructive",
      });
    },
  });

  const branch = policy.refundBranch;
  const isBlocked = branch === "blocked";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {branch === "refund" ? (
              "Cancel booking?"
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Late cancellation
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {branch === "refund" && (
                <>
                  <p>Are you sure you want to cancel this booking?</p>
                  {isFreeBooking ? (
                    <p className="text-muted-foreground">
                      This booking was free, so there's nothing to refund. Your seat will be released.
                    </p>
                  ) : (
                    <>
                      <p className="text-emerald-600 font-medium">
                        You'll get {estimatedCredits} credit{estimatedCredits === 1 ? "" : "s"} refunded to your account.
                      </p>
                      {stripePaid && (
                        <p className="text-muted-foreground">
                          Note: your refund is in Nimi credits, not to your card. Need a card refund? Contact us and we'll help.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {branch === "late" && (
                <>
                  <p>
                    This event is within the {policy.windowDays}-day cancellation window. If you cancel now, your seat will be released but <strong>no credits will be refunded</strong>.
                  </p>
                  {stripePaid && (
                    <p className="text-muted-foreground">
                      Any refund we do issue would be in credits, not to your card. If you need a card refund, contact us instead of cancelling here.
                    </p>
                  )}
                </>
              )}

              {isBlocked && (
                <p>
                  Cancellations aren't available for this booking. Please contact us for help.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelMutation.isPending}>
            {isBlocked ? "Close" : "Keep booking"}
          </AlertDialogCancel>
          {!isBlocked && (
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                cancelMutation.mutate();
              }}
              disabled={cancelMutation.isPending}
              className={branch === "late" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {cancelMutation.isPending
                ? "Cancelling..."
                : branch === "refund"
                  ? "Yes, cancel and refund"
                  : "Yes, release my seat"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
